import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
} from "recharts";
import {
  Home,
  TrendingDown,
  AlertTriangle,
  ExternalLink,
  MapPin,
  Building2,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Cite, SourcesList, tooltipStyle, type Source } from "./article-kit";

const zips = [
  {
    code: "36617",
    label: "Toulminville / Trinity Gardens",
    medianSold: 76854,
    low: 18950,
    high: 289000,
    listings: 90,
    sales: 142,
    foreclosure: 9,
    reo: 3,
    auction: 6,
    sourceUrl: "https://www.realtytrac.com/market-trends/mobile-al-36617/",
    source: "Realtytrac",
    citeN: 1,
  },
  {
    code: "36603",
    label: "Downtown Mobile",
    medianSold: 115000,
    low: 19000,
    high: 438000,
    listings: 79,
    sales: 72,
    foreclosure: 1,
    reo: 1,
    auction: 0,
    sourceUrl: "https://www.realtytrac.com/market-trends/mobile-al-36603/",
    source: "Realtytrac",
    citeN: 2,
  },
  {
    code: "36605",
    label: "Down the Bay / South Mobile",
    medianSold: 100000,
    low: 15000,
    high: 2300000,
    listings: 389,
    sales: 448,
    foreclosure: 19,
    reo: 5,
    auction: 14,
    sourceUrl: "https://www.realtor.com/realestateandhomes-search/36605",
    source: "Realtor.com",
    citeN: 3,
  },
];

const MOBILE_MEDIAN = 215000; // City-wide reference for context

const fmt = (n: number) => `$${n.toLocaleString()}`;
const fmtK = (n: number) =>
  n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`;

const medianData = zips.map((z) => ({
  code: z.code,
  Median: z.medianSold,
  label: z.label,
}));

const rangeData = zips.map((z) => ({
  code: z.code,
  Low: z.low,
  Median: z.medianSold,
  High: z.high,
}));

const distressData = zips.map((z) => ({
  code: z.code,
  Foreclosure: z.foreclosure,
  REO: z.reo,
  Auction: z.auction,
}));

const activityData = zips.map((z) => ({
  code: z.code,
  Listings: z.listings,
  "12-Mo Sales": z.sales,
}));

const caseStudies = [
  {
    place: "Detroit, MI",
    program: "Rehabbed & Ready",
    lift: "Nearby home values rose",
    takeaway: "Targeted rehab + mortgage-ready inventory lifts appraisals in cash-only markets.",
    url: "https://www.bridgedetroit.com/wp-content/uploads/2021/04/RR-Case-Study-20210225-V11-1.pdf",
    source: "U-Michigan / Bridge Detroit",
    tag: "Rehab pipeline",
  },
  {
    place: "Zanesville, OH",
    program: "CIC Rehabilitation Pipeline",
    lift: "Nonprofit acquires + rehabs",
    takeaway: "A municipal nonprofit can move faster than city government on acquisition and resale.",
    url: "https://www.greaterohio.org/good-ideas/2025/5/5/zanesvilles-housing-rehabilitation-pipeline-from-destruction-to-production",
    source: "Greater Ohio Policy Center",
    tag: "Nonprofit dev arm",
  },
  {
    place: "East Liverpool, OH",
    program: "Neighborhood Stabilization Strategy",
    lift: "Parcel-level triage",
    takeaway: "Parcel-level triage is the prerequisite to any funding stack.",
    url: "https://bettercity.us/wp-content/uploads/2023/04/Neighborhood-Stabilization-Final.pdf",
    source: "Better City",
    tag: "Planning",
  },
  {
    place: "Superior, WI",
    program: "Vacant to Value ($1 homes)",
    lift: "$1 disposition",
    takeaway: "Aggressive disposition policies work when paired with rehab capital and occupancy covenants.",
    url: "https://www.wpr.org/news/superior-vacant-to-value-neighborhood-blight-property",
    source: "Wisconsin Public Radio",
    tag: "Disposition policy",
  },
  {
    place: "Chicago, IL",
    program: "CDFI Collaborative",
    lift: "Pooled rehab capital",
    takeaway: "CDFI collaboration unlocks deal flow that no single lender will underwrite alone.",
    url: "https://www.jchs.harvard.edu/sites/default/files/media/imp/von_hoffman_arck_chicago_case_study_2017.pdf",
    source: "Harvard JCHS",
    tag: "Pooled capital",
  },
  {
    place: "HUD Nationwide",
    program: "Neighborhood Stabilization Program",
    lift: "2,500+ grantees studied",
    takeaway: "Evidence base for designing the funding stack and measurement plan.",
    url: "https://www.huduser.gov/publications/pdf/neighborhood_stabilization.pdf",
    source: "HUD PD&R",
    tag: "Evidence base",
  },
];

const bestBuys = [
  {
    title: "Parcel-level triage first",
    body: "Survey every distressed parcel in 36617, 36603, and 36605. Sort into demolish / rehab / hold before any capital moves.",
    model: "East Liverpool, OH",
  },
  {
    title: "Rehab-and-resale pipeline at appraisal",
    body: "Stand up a Detroit-style \"Rehabbed & Ready\" through the city land bank — renovate to standard, sell at appraisal.",
    model: "Detroit, MI",
  },
  {
    title: "Pooled CDFI rehab fund",
    body: "Convene Alabama and Gulf Coast CDFIs to pool small-balance rehab capital. Single-lender underwriting won't clear this market.",
    model: "Chicago, IL",
  },
  {
    title: "Disposition with occupancy covenants",
    body: "For city-owned and tax-delinquent inventory, use $1 disposition tied to rehab + owner-occupancy covenants.",
    model: "Superior, WI",
  },
];

const sources: Source[] = [
  { n: 1, label: "Realtytrac — Mobile, AL 36617 market trends", url: "https://www.realtytrac.com/market-trends/mobile-al-36617/" },
  { n: 2, label: "Realtytrac — Mobile, AL 36603 market trends", url: "https://www.realtytrac.com/market-trends/mobile-al-36603/" },
  { n: 3, label: "Realtor.com — 36605 real estate listings", url: "https://www.realtor.com/realestateandhomes-search/36605" },
  { n: 4, label: "University of Michigan / Bridge Detroit — Rehabbed & Ready case study", url: "https://www.bridgedetroit.com/wp-content/uploads/2021/04/RR-Case-Study-20210225-V11-1.pdf" },
  { n: 5, label: "Greater Ohio Policy Center — Zanesville's Housing Rehabilitation Pipeline", url: "https://www.greaterohio.org/good-ideas/2025/5/5/zanesvilles-housing-rehabilitation-pipeline-from-destruction-to-production" },
  { n: 6, label: "HUD PD&R — Neighborhood Stabilization Program evaluation", url: "https://www.huduser.gov/publications/pdf/neighborhood_stabilization.pdf" },
  { n: 7, label: "Better City — East Liverpool Neighborhood Stabilization Strategy", url: "https://bettercity.us/wp-content/uploads/2023/04/Neighborhood-Stabilization-Final.pdf" },
  { n: 8, label: "Wisconsin Public Radio — Superior Vacant to Value", url: "https://www.wpr.org/news/superior-vacant-to-value-neighborhood-blight-property" },
  { n: 9, label: "Harvard JCHS — Chicago CDFI Collaborative case study", url: "https://www.jchs.harvard.edu/sites/default/files/media/imp/von_hoffman_arck_chicago_case_study_2017.pdf" },
];

const ChartCard = ({
  title,
  subtitle,
  children,
  footnote,
  sources: chartSources,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footnote?: string;
  sources?: number[];
}) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <h4 className="font-display font-bold text-base text-foreground">
      {title}
      {chartSources && chartSources.length > 0 && <Cite ns={chartSources} />}
    </h4>
    {subtitle && <p className="text-xs text-muted-foreground mt-1 mb-4">{subtitle}</p>}
    <div className="h-[260px] w-full">{children}</div>
    {footnote && <p className="text-[11px] text-muted-foreground mt-3 italic">{footnote}</p>}
  </div>
);

const PriorityZipCodesArticle = () => {
  return (
    <div className="space-y-12 not-prose">
      {/* Lead */}
      <p className="text-lg text-muted-foreground leading-relaxed">
        In three Mobile, Alabama zip codes — <strong className="text-foreground">36617, 36603, and 36605</strong> —
        the lowest single-family listings still trade below $25,000, and median sold prices sit
        well under the city's overall figure. The valuation gap isn't cosmetic: it shapes what a
        homeowner can borrow against, what a small business can collateralize, and which
        neighborhoods qualify for the financing stacks that fund growth elsewhere in the city.
        <Cite ns={[1, 2, 3]} />
      </p>

      {/* Headline KPIs */}
      <div className="grid sm:grid-cols-3 gap-4">
        {zips.map((z, i) => (
          <motion.div
            key={z.code}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="bg-card border border-border rounded-xl p-5 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-bl-full" />
            <Badge variant="outline" className="font-mono text-xs mb-3">
              ZIP {z.code}
            </Badge>
            <p className="text-xs text-muted-foreground leading-snug">{z.label}</p>
            <p className="font-display text-3xl font-bold mt-3 text-foreground">
              {fmt(z.medianSold)}
              <Cite ns={[z.citeN]} />
            </p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider mt-1">Median sold price</p>
            <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="font-bold text-sm text-foreground">{z.listings}</p>
                <p className="text-[10px] text-muted-foreground">Listings</p>
              </div>
              <div>
                <p className="font-bold text-sm text-foreground">{z.sales}</p>
                <p className="text-[10px] text-muted-foreground">12-mo sales</p>
              </div>
              <div>
                <p className="font-bold text-sm text-foreground">{z.foreclosure + z.reo + z.auction}</p>
                <p className="text-[10px] text-muted-foreground">Distressed</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Section: Data */}
      <section>
        <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">The data</span>
        <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 mb-2 text-foreground">
          Three ZIPs, one suppressed market
        </h2>
        <p className="text-muted-foreground mb-6 max-w-2xl">
          May 2026 snapshots from Realtytrac and Realtor.com. Deep-discount inventory sits next to
          mid-priced sales, distress is concentrated, and turnover is uneven.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <ChartCard
            title="Median sold price vs. Mobile city median"
            subtitle="All three priority ZIPs trade well below the citywide reference"
            footnote="Reference line is a stylized citywide median for visual comparison."
            sources={[1, 2, 3]}
          >
            <ResponsiveContainer>
              <BarChart data={medianData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={(v) => fmtK(v)}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v: number) => fmt(v)}
                  cursor={{ fill: "hsl(var(--muted) / 0.2)" }}
                />
                <ReferenceLine
                  y={MOBILE_MEDIAN}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="4 4"
                  label={{
                    value: `Mobile ~${fmtK(MOBILE_MEDIAN)}`,
                    position: "insideTopRight",
                    fill: "hsl(var(--primary))",
                    fontSize: 10,
                  }}
                />
                <Bar dataKey="Median" radius={[6, 6, 0, 0]}>
                  {medianData.map((_, i) => (
                    <Cell key={i} fill="hsl(var(--primary))" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Listed price range (low → high)"
            subtitle="The spread inside a single ZIP — the appraisal gap in one chart"
            footnote="Log scale. 36605's $2.3M upper bound reflects waterfront outliers."
            sources={[1, 2, 3]}
          >
            <ResponsiveContainer>
              <ComposedChart data={rangeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis
                  scale="log"
                  domain={[10000, 3000000]}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={(v) => fmtK(v)}
                  ticks={[10000, 50000, 100000, 500000, 1000000, 3000000]}
                />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Low" fill="hsl(var(--primary) / 0.35)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Median" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="High" fill="hsl(var(--primary) / 0.6)" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Distressed inventory by type"
            subtitle="Foreclosure, REO, and auction concentration"
            sources={[1, 2, 3]}
          >
            <ResponsiveContainer>
              <BarChart data={distressData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Foreclosure" stackId="a" fill="hsl(var(--primary))" />
                <Bar dataKey="REO" stackId="a" fill="hsl(var(--primary) / 0.65)" />
                <Bar dataKey="Auction" stackId="a" fill="hsl(var(--primary) / 0.35)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Market activity"
            subtitle="Active listings vs. trailing 12-month sales"
            sources={[1, 2, 3]}
          >
            <ResponsiveContainer>
              <BarChart data={activityData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted) / 0.2)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Listings" fill="hsl(var(--primary) / 0.5)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="12-Mo Sales" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Per-ZIP source links */}
        <div className="grid sm:grid-cols-3 gap-2 mt-4">
          {zips.map((z) => (
            <a
              key={z.code}
              href={z.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5 border border-border rounded-lg px-3 py-2"
            >
              <MapPin className="h-3 w-3 text-primary" />
              ZIP {z.code} source: {z.source}
              <ExternalLink className="h-3 w-3 ml-auto" />
            </a>
          ))}
        </div>
      </section>

      {/* Why valuations stay suppressed */}
      <section>
        <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
          Why valuations stay suppressed
        </span>
        <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 mb-6 text-foreground">
          Three forces compound in these ZIPs
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: TrendingDown,
              title: "The appraisal gap",
              body: "When comps include a cluster of sub-$25K cash sales, even a renovated home appraises low. The gap blocks mortgages, pushes deals back to cash, and reinforces the discount in the next round of comps.",
              cite: "HUD NSP evaluation",
              citeN: 6,
            },
            {
              icon: AlertTriangle,
              title: "Concentrated distress",
              body: "Foreclosure, REO, and auction inventory cluster in a small share of parcels but drag prices on every neighbor. Parcel-level surveys are the prerequisite for any stabilization plan.",
              cite: "East Liverpool, OH",
              citeN: 7,
            },
            {
              icon: Building2,
              title: "Thin financing pipes",
              body: "Small-balance mortgages (under $70K) are uneconomic for most lenders. Without CDFI capital or pooled rehab funds, the only buyers left are investors paying cash at the bottom of the market.",
              cite: "Harvard JCHS",
              citeN: 9,
            },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <f.icon className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-display font-bold text-base text-foreground mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              <p className="text-[11px] text-primary mt-3 font-medium">
                {f.cite}
                <Cite ns={[f.citeN]} />
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What's working elsewhere */}
      <section>
        <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
          What's working elsewhere
        </span>
        <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 mb-2 text-foreground">
          Six programs that moved valuations
        </h2>
        <p className="text-muted-foreground mb-6 max-w-2xl">
          None is a silver bullet. Together they sketch a playbook for low-cost, high-vacancy
          neighborhoods.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {caseStudies.map((c, i) => (
            <motion.a
              key={c.program}
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-colors block"
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="text-[10px]">
                  {c.place}
                </Badge>
                <Badge className="text-[10px] bg-primary/10 text-primary border-0 hover:bg-primary/15">
                  {c.tag}
                </Badge>
              </div>
              <h4 className="font-display font-bold text-base mb-1 text-foreground group-hover:text-primary transition-colors">
                {c.program}
              </h4>
              <p className="text-[11px] text-muted-foreground mb-3">{c.lift}</p>
              <p className="text-sm text-foreground/80 border-l-2 border-primary/40 pl-3 italic mb-3">
                {c.takeaway}
              </p>
              <span className="text-xs text-primary flex items-center gap-1 font-medium">
                {c.source}
                <ExternalLink className="h-3 w-3 ml-1" />
              </span>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Four best buys */}
      <section>
        <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
          Four best buys for Mobile
        </span>
        <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 mb-6 text-foreground">
          Mapping the cross-city playbook onto three ZIPs
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {bestBuys.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-card border border-border rounded-xl p-5 flex gap-4"
            >
              <div className="shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="font-display font-bold text-primary">{i + 1}</span>
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-base text-foreground mb-1.5">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{b.body}</p>
                <div className="flex items-center gap-1.5 text-[11px] text-primary">
                  <CheckCircle2 className="h-3 w-3" />
                  Modeled on {b.model}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Policy asks */}
      <section className="bg-card border border-border rounded-xl p-6 md:p-8">
        <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">Policy asks</span>
        <h2 className="font-display text-2xl font-bold mt-2 mb-4 text-foreground">
          Who needs to move, and what they need to do
        </h2>
        <ul className="space-y-3">
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">City of Mobile:</strong> Fund a parcel-level survey
              of 36617, 36603, and 36605 and publish it as open data. Create a disposition policy
              that prioritizes owner-occupants over speculative buyers.
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Funders & banks:</strong> Capitalize a Mobile-area
              rehab pool with first-loss reserves to make small-balance loans viable. Use HUD's NSP
              evaluation as the measurement baseline.
              <Cite ns={[6]} />
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Small businesses:</strong> Site or expand in these
              ZIPs, get DBE-certified to bid on rehab and demolition contracts, and hire locally —
              every dollar that lands in a priority ZIP strengthens the next round of comps.
            </span>
          </li>
        </ul>
      </section>

      {/* Closing */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          A clear path forward
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          The data in 36617, 36603, and 36605 isn't a verdict; it's a starting line. Cities with
          the same conditions — Detroit, Zanesville, East Liverpool, Superior, Chicago — moved
          valuations by combining parcel-level intelligence, mortgage-ready rehab inventory, pooled
          CDFI capital, and disposition tied to occupancy. Mobile has every piece of that playbook
          available. What it lacks is the assembly.
        </p>
      </section>

      <SourcesList sources={sources} />
    </div>
  );
};

export default PriorityZipCodesArticle;
export const PRIORITY_ZIP_SLUG =
  "mobile-priority-zip-codes-where-home-values-stay-below-50k";